import React, { useEffect, useState } from "react"
import { Button, Col, Form, Row } from "react-bootstrap"
import { injectIntl } from "react-intl"
import { useDispatch, useSelector } from "react-redux"
import { Redirect } from "react-router"
import { Link } from "react-router-dom"
import Select from "react-select"
import ConfirmButton from "../../common/button/confirmbutton.component"
import Icon from "../../common/icon/icon.component"
import { SelectOption } from "../../common/types"
import IntlProps from "../../state/helpers/IntlProps"
import { InventoryLocationAction } from "../../state/inventory/location/actions"
import { InventoryLocationSelector } from "../../state/inventory/location/selectors"
import Location from "../../state/location/Location"
import LocationService from "../../state/location/LocationService"
import { CREATE_NEW_STOCK_LOCATION_PAGE_MESSAGES, EDIT_STOCK_LOCATION_PAGE_MESSAGES } from "../../utils/inventory"
import { mapAvailableLocationsToOptions } from "../../utils/inventory/utils"

const StockInventoryForm = (props: IntlProps) => {
    const dispatch = useDispatch()
    const state = useSelector(InventoryLocationSelector.get)
    const [locationOption, setLocationOption] = useState<Array<SelectOption<string>>>([]) 
    const [validated, setValidated] = useState(false)

    useEffect(() => {
        if (!state.listing || state.listing.length === 0) {
            dispatch(InventoryLocationAction.fetchListing())
        }

        LocationService.fetchAllLocations()
            .then((res) => {
                return  (Object.assign(res) as Location[])
            }).then( (physical) => {
                if (physical) {
                    const allLocations = mapAvailableLocationsToOptions(physical, [])
                    if (state.listing) { 
                        const usedLocationIds = state.listing
                            .filter(location => location.id !== state.id)
                            .map(location => location.locationIds)
                            .reduce((acc, ids) => acc.concat(ids), [])

                        const availLocations = allLocations.filter(location => !usedLocationIds.includes(location.value))
                        setLocationOption(availLocations)

                    }
                }})
        return () => {
            dispatch(InventoryLocationAction.clearLocation())
        }
    }, [])

    const handleSubmit = (event) => {
        event.preventDefault()
        const form = event.currentTarget
        if (form.checkValidity() === false) {
            event.stopPropagation()
            setValidated(true)
            return
        }
        dispatch(InventoryLocationAction.submit())
    }

    const deleteProduct = () => {
        dispatch(InventoryLocationAction.delete())
    }
    
    const handleNameChange = (event) => {
        setValidated(false)
        const newName = event.target.value
        dispatch(InventoryLocationAction.setStockInventoryName(newName))
    }

    const handleAssignedLocationChange = (selectedOptions) => {
        setValidated(false)
        const selectedLocationIds = selectedOptions.map(option => option.value)
        dispatch(InventoryLocationAction.setLocation(selectedLocationIds))
    }
    
    if (state.fetchSuccess) {
        return <Redirect to="/inventory/stock-locations" />
    }

    return (
        <>
            <Form noValidate className="ui-has-modules" onSubmit={handleSubmit} validated={validated} >
                <div className="form-group">
                    <Col className="ui-module">
                        <Form.Group >
                            <Form.Label htmlFor="name"> 
                                {props.intl.formatMessage(CREATE_NEW_STOCK_LOCATION_PAGE_MESSAGES["nameLabel"])}
                            </Form.Label >
                            <Form.Control 
                                type="text" 
                                value={state.name}
                                placeholder={props.intl.formatMessage(CREATE_NEW_STOCK_LOCATION_PAGE_MESSAGES["namePlaceholder"])}
                                onChange={handleNameChange}
                                required
                                isInvalid={validated && state.name.trim() === ""}
                            />
                            <Form.Control.Feedback type="invalid">
                                {props.intl.formatMessage(CREATE_NEW_STOCK_LOCATION_PAGE_MESSAGES["nameMissingError"])}
                            </Form.Control.Feedback>
                            <small className="form-text text-muted">
                                {props.intl.formatMessage(CREATE_NEW_STOCK_LOCATION_PAGE_MESSAGES["nameHint"])}
                            </small>
                        </Form.Group>
                        <Form.Group>
                            <Form.Label htmlFor="location">
                                {props.intl.formatMessage(CREATE_NEW_STOCK_LOCATION_PAGE_MESSAGES["assignedLocationLabel"])}
                            </Form.Label>
                            <Select 
                                multi={true}
                                value={state.locationIds}
                                options={locationOption}
                                onChange={handleAssignedLocationChange} 
                                required
                            />
                            {validated && state.locationIds.length === 0 && 
                                <small className="text-danger">
                                    {props.intl.formatMessage(CREATE_NEW_STOCK_LOCATION_PAGE_MESSAGES["locationsMissingError"])}
                                </small>}
                            <small className="form-text text-muted">
                                {props.intl.formatMessage(CREATE_NEW_STOCK_LOCATION_PAGE_MESSAGES["assignedLocationHint"])}
                            </small>
                        </Form.Group>
                    </Col>
                </div>
                <Row style={{zIndex:10000} }  className="m-0 py-1 fixed-bottom pr-0 pr-md-5 pr-xl-0">
                    <Col lg={12} xl={11} className="mx-auto pt-3 pl-4 pr-4 pr-md-5 pr-xl-4">
                        <Col className="ui-module sticky-bottom-module">
                            <Row>
                                <Col xs={5} md={6}>
                                    {state.id && state.listing.length > 1 && 
                                    <ConfirmButton
                                        onClick={deleteProduct}
                                        className="btn btn-danger">
                                        {props.intl.formatMessage(EDIT_STOCK_LOCATION_PAGE_MESSAGES["deleteLabel"])}
                                    </ConfirmButton>}
                                </Col>
                                <Col xs={7} md={6} className="text-align-right">
                                    <Link to="/inventory/stock-locations" className="btn btn-light mr-2">
                                        {props.intl.formatMessage(CREATE_NEW_STOCK_LOCATION_PAGE_MESSAGES["cancelLabel"])}
                                    </Link>
                                    <Button type="submit"> 
                                        {props.intl.formatMessage(CREATE_NEW_STOCK_LOCATION_PAGE_MESSAGES["saveLabel"])}
                                    </Button>
                                </Col>
                            </Row>
                        </Col>
                    </Col>
                </Row>
                {state.isError &&
                    <div className="text-danger mb-2 p-2 text-right">
                        <small>
                            <Icon value="exclamation" className="mr-1" />
                            {state.errorMessage? 
                                props.intl.formatMessage(CREATE_NEW_STOCK_LOCATION_PAGE_MESSAGES["duplicateNameErrorMessage"]):
                                props.intl.formatMessage(CREATE_NEW_STOCK_LOCATION_PAGE_MESSAGES["genericErrorMessage"])}
                        </small>
                    </div>
                }
            </Form>
        </>
    )
}

export default injectIntl(StockInventoryForm)


// WEBPACK FOOTER //
// ./src/inventory/forms/StockLocationForm.tsx